import firebase from 'firebase';
import { formatDateSeconds } from './date';
import startCase from 'lodash.startcase';

export async function exportReports(uid: string, firestore: firebase.firestore.Firestore) {
  const reports = await loadReports(uid, firestore);

  const contentFields = [
    ...reports.reduce(
      (set, report) => new Set([...Object.keys(report.contents), ...set]),
      new Set<string>()
    )
  ];
  const reportExportSpec: Record<string, (report: Report) => string> = {
    date: report => report.date,
    riderName: report => report.rider.name,
    bikeName: report => report.bike.name,
    bikeEngine: report => report.bike.engine,
    reportCreatedAt: report => formatDateSeconds(report.createdAt),
    reportUpdatedAt: report => formatDateSeconds(report.updatedAt),
    sessionsDurationTotal: report =>
      report.sessions
        .map(session => (typeof session === 'number' ? session : 0))
        .reduce((current, total) => current + total, 0)
        .toString(),
    sessionDurations: report =>
      report.sessions.map(session => session.duration?.toString() ?? '').join(' | '),
    sessionComments: report =>
      report.sessions.map(session => session.comment?.toString() ?? '').join(' | ')
  };
  const reportFields = Object.keys(reportExportSpec);
  const exportedReportsData = reports.map(report => [
    ...reportFields.map(field => reportExportSpec[field](report)),
    ...contentFields.map(field => report.contents[field])
  ]);
  const exportedReports = [[...reportFields, ...contentFields].map(startCase), ...exportedReportsData];
  console.log(exportedReports);
  return exportedReports;
}

async function loadReports(uid: string, firestore: firebase.firestore.Firestore) {
  const [ridersSnapshot, bikesSnapshot, reportsSnapshot] = await Promise.all([
    (firestore.collection(`users/${uid}/riders`) as Collection<RawRider>).get(),
    (firestore.collection(`users/${uid}/bikes`) as Collection<RawBike>).get(),
    (firestore.collection(`users/${uid}/reports`).orderBy('date', 'desc') as Collection<RawReport>).get()
  ] as const);
  const ridersLookup = new Map(ridersSnapshot.docs.map(s => [s.id, transformRider(s.data())]));
  const bikesLookup = new Map(
    bikesSnapshot.docs.map(s => [s.id, transformBike(s.data(), ridersLookup)])
  );
  return reportsSnapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() } as RawReport))
    .map(report => transformReport(report, ridersLookup, bikesLookup));
}

function transformRider(rider: RawRider) {
  return { ...rider, createdAt: rider.createdAt.toDate(), updatedAt: rider.updatedAt.toDate() };
}

function transformBike(bike: RawBike, ridersLookup: Map<string, Rider>) {
  return {
    ...bike,
    rider: ridersLookup.get(bike.rider)!,
    createdAt: bike.createdAt.toDate(),
    updatedAt: bike.updatedAt.toDate()
  };
}

function transformReport(
  report: RawReport,
  ridersLookup: Map<string, Rider>,
  bikesLookup: Map<string, Bike>
) {
  return {
    ...report,
    rider: ridersLookup.get(report.rider)!,
    bike: bikesLookup.get(report.bike)!,
    date: report.date,
    createdAt: report.createdAt.toDate(),
    updatedAt: report.updatedAt.toDate(),
    sessions: report.sessions.map(session => JSON.parse(session)) as Session[]
  };
}

export interface RawRider {
  id: string;
  name: string;
  email: string;
  createdAt: firebase.firestore.Timestamp;
  updatedAt: firebase.firestore.Timestamp;
}

export interface RawBike {
  id: string;
  name: string;
  engine: '2-stroke' | '4-stroke';
  createdAt: firebase.firestore.Timestamp;
  updatedAt: firebase.firestore.Timestamp;
  notes: string;
  rider: string;
}

export interface RawReport {
  id: string;
  rider: string;
  bike: string;
  contents: Record<string, string>;
  sessions: string[];
  date: string;
  createdAt: firebase.firestore.Timestamp;
  updatedAt: firebase.firestore.Timestamp;
}

export type Rider = ReturnType<typeof transformRider>;
export type Bike = ReturnType<typeof transformBike>;
export type Report = ReturnType<typeof transformReport>;
export interface Session {
  duration?: string | number;
  comment?: string;
}

export type Collection<T> = firebase.firestore.CollectionReference<T>;
